import { Icon, InputGroup, Intent, Spinner } from "@blueprintjs/core";
import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import { ILocation, ILocationValue } from "components/Template/types";
import { doDisabled, doHidden, doInvalid } from "components/Template/utils";
import Validate from "components/Template/Validate";
import { IInputProps } from "../types";
import { hasAwsLocationConfiguration, searchLocation, suggestLocations } from "./awsLocation";
import "./Location.scss";

interface LocationProps extends IInputProps {
  template: ILocation;
}

const EMPTY_LOCATION: ILocationValue = { label: "" };

const Location = (props: LocationProps) => {
  const { template, mutators } = props;
  const value = (mutators.getValue(template.name) as ILocationValue | undefined) || EMPTY_LOCATION;
  const [query, setQuery] = useState(value.label || "");
  const [suggestions, setSuggestions] = useState<Array<string>>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string>();
  const selectingRef = useRef(false);
  const mountedRef = useRef(true);
  const disabled = !mutators.handleChange || doDisabled(props);
  const invalid = doInvalid(props);
  const hasResolvedPosition = Boolean(value.position);

  useEffect(() => {
    if (!selectingRef.current) setQuery(value.label || "");
  }, [value.label]);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (disabled || trimmed.length < 3 || (trimmed === value.label && hasResolvedPosition)) {
      setSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      suggestLocations(trimmed, template.center, controller.signal)
        .then((items) => {
          if (mountedRef.current) setSuggestions(items);
        })
        .catch((requestError: any) => {
          if (mountedRef.current && requestError?.name !== "AbortError") setError(requestError?.message || "Unable to load suggestions.");
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [disabled, hasResolvedPosition, query, template.center, value.label]);

  const selectLocation = async (label: string) => {
    selectingRef.current = true;
    setQuery(label);
    setSuggestions([]);
    setSearching(true);
    setError(undefined);
    try {
      const resolved = await searchLocation(label, template.center);
      if (!mountedRef.current) return;
      mutators.handleChange?.(template.name, resolved);
      setQuery(resolved.label);
    } catch (requestError: any) {
      if (mountedRef.current) setError(requestError?.message || "Unable to resolve this location.");
    } finally {
      selectingRef.current = false;
      if (mountedRef.current) setSearching(false);
    }
  };

  if (doHidden(props)) return null;

  const intent = error || invalid ? Intent.DANGER : value.position ? Intent.SUCCESS : Intent.NONE;
  const rightElement = searching
    ? <span className="location-input__status"><Spinner size={16} /></span>
    : value.position
      ? <span className="location-input__status" title="Location resolved"><Icon icon="tick-circle" intent={Intent.SUCCESS} /></span>
      : undefined;

  return (
    <Validate
      {...props}
      className={`location-input anchor-${template.name}`}
      showLabel
      showInfo
      {...(error ? { intent: Intent.DANGER, helperText: error } : {})}
    >
      <div className="location-input__control">
        <div
          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" && query.trim() && !searching) {
              event.preventDefault();
              selectLocation(query.trim());
            }
          }}
      >
          <InputGroup
            id={mutators.getPath(template.name)}
            leftIcon="map-marker"
            placeholder="Search address or place"
            value={query}
            disabled={disabled}
            intent={intent}
            rightElement={rightElement}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const nextLabel = event.target.value;
              setQuery(nextLabel);
              mutators.handleChange?.(template.name, { label: nextLabel });
              setError(undefined);
            }}
            onBlur={() => setSuggestions([])}
          />
          {suggestions.length > 0 && (
            <div className="location-input__suggestions" role="listbox" aria-label="Location suggestions">
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion}-${index}`}
                  type="button"
                  role="option"
                  aria-selected="false"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectLocation(suggestion)}
                >
                  <Icon icon="map-marker" />
                  <span>{suggestion}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {!hasAwsLocationConfiguration && <div className="location-input__configuration">Amazon Location is not configured.</div>}
      </div>
    </Validate>
  );
};

export default Location;
